export function dist(p1, p2) {
  const dx = p2[0] - p1[0];
  const dy = p2[1] - p1[1];
  return Math.hypot(dx, dy);
}

export function linspace(start, stop, num) {
  const step = (stop - start) / (num - 1);
  return Array.from({ length: num }, (_, i) => start + step * i);
}

export function smoothPoints(points, windowSize = 5) {
  const smoothed = new Array(points.length);

  for (let i = 0; i < points.length; i++) {
    let sum = 0;
    let count = 0;

    for (
      let j = Math.max(0, i - windowSize);
      j < Math.min(points.length, i + windowSize + 1);
      j++
    ) {
      sum += points[j];
      count++;
    }

    smoothed[i] = sum / count;
  }

  return smoothed;
}

export function smoothPointsMultiple(points, windowSize = 5, passes = 2) {
  let result = points;
  for (let i = 0; i < passes; i++) {
    result = smoothPoints(result, windowSize);
  }
  return result;
}

export function calculatePolygonArea(points) {
  let area = 0;
  for (let i = 0; i < points.length - 1; i++) {
    area += points[i].x * points[i + 1].y;
    area -= points[i + 1].x * points[i].y;
  }
  return Math.abs(area) / 2;
}

export function lineIntersection(x1, y1, x2, y2, x3, y3, x4, y4) {
  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (denominator === 0) return null;

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denominator;

  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    return {
      x: x1 + t * (x2 - x1),
      y: y1 + t * (y2 - y1),
    };
  }

  return null;
}

export function findIntersection(points, cutoffs) {
  const minDistance = 30;
  const intersections = [];
  const intersectionSegments = [];
  const minSegmentDistance = 10;
  const totalPoints = points.length;
  const totalRiverPoints = (points.length - 1) / 2;

  // Spatial hash grid — O(n) average instead of O(n²)
  const cellSize = 40;
  let bMinX = Infinity,
    bMinY = Infinity;
  let bMaxX = -Infinity,
    bMaxY = -Infinity;
  for (let i = 0; i < totalPoints; i++) {
    const px = points[i].x,
      py = points[i].y;
    if (px < bMinX) bMinX = px;
    if (py < bMinY) bMinY = py;
    if (px > bMaxX) bMaxX = px;
    if (py > bMaxY) bMaxY = py;
  }

  const cols = Math.floor((bMaxX - bMinX) / cellSize) + 2;
  const grid = new Map();

  for (let i = 0; i < totalPoints - 1; i++) {
    const ax = points[i].x,
      ay = points[i].y;
    const bx = points[i + 1].x,
      by = points[i + 1].y;
    const minCx = Math.floor((Math.min(ax, bx) - bMinX) / cellSize);
    const maxCx = Math.floor((Math.max(ax, bx) - bMinX) / cellSize);
    const minCy = Math.floor((Math.min(ay, by) - bMinY) / cellSize);
    const maxCy = Math.floor((Math.max(ay, by) - bMinY) / cellSize);
    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cy = minCy; cy <= maxCy; cy++) {
        const key = cy * cols + cx;
        let bucket = grid.get(key);
        if (!bucket) {
          bucket = [];
          grid.set(key, bucket);
        }
        bucket.push(i);
      }
    }
  }

  const visited = new Int32Array(totalPoints);
  let gen = 0;

  for (let i = 5; i < totalPoints - 1; i++) {
    gen++;
    const ax = points[i].x,
      ay = points[i].y;
    const bx = points[i + 1].x,
      by = points[i + 1].y;
    const minCx = Math.floor((Math.min(ax, bx) - bMinX) / cellSize);
    const maxCx = Math.floor((Math.max(ax, bx) - bMinX) / cellSize);
    const minCy = Math.floor((Math.min(ay, by) - bMinY) / cellSize);
    const maxCy = Math.floor((Math.max(ay, by) - bMinY) / cellSize);

    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cy = minCy; cy <= maxCy; cy++) {
        const key = cy * cols + cx;
        const bucket = grid.get(key);
        if (!bucket) continue;
        for (let bi = 0; bi < bucket.length; bi++) {
          const j = bucket[bi];
          if (j <= i + minSegmentDistance) continue;
          if (j > totalPoints - 5) continue;
          if (visited[j] === gen) continue;
          visited[j] = gen;

          const intersection = lineIntersection(
            ax,
            ay,
            bx,
            by,
            points[j].x,
            points[j].y,
            points[j + 1].x,
            points[j + 1].y,
          );

          if (intersection) {
            const isUnique = intersections.every(
              (existing) =>
                Math.hypot(
                  existing.x - intersection.x,
                  existing.y - intersection.y,
                ) > minDistance,
            );

            if (isUnique) {
              let a = i;
              if (a > totalRiverPoints) a = totalRiverPoints * 2 - a - 1;
              let b = j;
              if (b > totalRiverPoints) b = totalRiverPoints * 2 - b - 1;
              let start = Math.min(a, b);
              let end = Math.max(a, b);
              intersections.push(intersection);
              intersectionSegments.push({
                start: start,
                end: end,
                startR: i,
                endR: j,
                type: "self",
              });
            }
          }
        }
      }
    }
  }

  return { intersections, intersectionSegments };
}

export function drawVectors(ctx, x, y, vectors, color = "red", scale = 20) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;

  for (let i = 0; i < x.length; i++) {
    ctx.beginPath();
    ctx.moveTo(x[i], y[i]);
    ctx.lineTo(x[i] + vectors[i][0] * scale, y[i] + vectors[i][1] * scale);
    ctx.stroke();
  }
}

export function drawVectorsCentered(
  ctx,
  x,
  y,
  vectors,
  color = "red",
  scale = 20,
) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;

  for (let i = 0; i < x.length; i++) {
    ctx.beginPath();
    // Start from half the vector length in one direction
    ctx.moveTo(
      x[i] - vectors[i][0] * scale * 0.5,
      y[i] - vectors[i][1] * scale * 0.5,
    );
    // End at half the vector length in the other direction
    ctx.lineTo(
      x[i] + vectors[i][0] * scale * 0.5,
      y[i] + vectors[i][1] * scale * 0.5,
    );
    ctx.stroke();
  }
}

export function constrainVector(vec, maxLength) {
  const length = Math.hypot(vec[0], vec[1]);
  if (length === 0) return vec; // Handle zero vector case
  if (length > maxLength) {
    return [(vec[0] / length) * maxLength, (vec[1] / length) * maxLength];
  }
  return vec;
}

export function computeTangents(x, y, maxVectorLength) {
  const tangents = [];
  for (let i = 0; i < x.length; i++) {
    let dx, dy;
    if (i === 0) {
      dx = x[1] - x[0];
      dy = y[1] - y[0];
    } else if (i === x.length - 1) {
      dx = x[i] - x[i - 1];
      dy = y[i] - y[i - 1];
    } else {
      dx = x[i + 1] - x[i - 1];
      dy = y[i + 1] - y[i - 1];
    }

    const unconstrained = [dx, dy];
    tangents.push(constrainVector(unconstrained, maxVectorLength));
  }
  return tangents;
}

export function calculateCurvature(x, y) {
  const curvature = Array(x.length).fill(0);

  for (let i = 1; i < x.length - 1; i++) {
    const dx1 = x[i] - x[i - 1];
    const dy1 = y[i] - y[i - 1];
    const dx2 = x[i + 1] - x[i];
    const dy2 = y[i + 1] - y[i];

    // angle between vectors
    const angle = Math.atan2(dx1 * dy2 - dy1 * dx2, dx1 * dx2 + dy1 * dy2);

    // normalize curvature to prevent extreme values
    curvature[i] = Math.tanh(angle); // This will keep values between -1 and 1
  }

  // Smooth out curvatures at extremities
  const L = x.length;
  const endPoints = Math.floor(L * 0.05);
  for (let i = 0; i < endPoints; i++) {
    const t = i / endPoints;
    curvature[i] = curvature[i] * t;
  }
  for (let i = L - endPoints; i < L; i++) {
    const t = (L - i) / endPoints;
    curvature[i] = curvature[i] * t;
  }

  // Apply smoothing to prevent sudden jumps
  return smoothPointsMultiple(curvature, 3, 2);
}

export function computeBitangents(tangents, curvature, maxVectorLength) {
  return tangents.map((t, i) => {
    const perpX = -t[1];
    const perpY = t[0];
    const scale = Math.abs(curvature[i]);
    const sign = Math.sign(curvature[i]);
    const unconstrained = [perpX * scale * sign, perpY * scale * sign];
    // Apply stricter constraint and ensure W is positive
    return constrainVector(unconstrained, maxVectorLength);
  });
}
